import React from 'react';
import { ChevronDown, Target } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { AnimatedNumber, useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  MerchantDot,
  ProductMockupBody,
  ProductMockupFrame
} from './shared';

const Toolbar = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const CampaignIdentity = styled('div')`
  .name {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
    margin-bottom: 5px;
  }

  .merchant {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #475569;
  }
`;

const TypeBadge = styled('span')`
  font-size: 9px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 4px;
  padding: 2px 6px;
`;

const ToolbarRight = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #389e0d;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #52c41a;
  }
`;

const ToolbarButton = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 500;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  white-space: nowrap;
`;

const CardsRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MetricCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 10px 12px;

  .label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 9.5px;
    color: #94a3b8;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .value {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
    white-space: nowrap;
  }

  .caption {
    font-size: 9.5px;
    color: #94a3b8;
    margin-top: 2px;
    white-space: nowrap;
  }
`;

const ChartRow = styled('div')`
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 8px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 12px;

  .heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 8px;

    .legend {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-weight: 500;
      color: #94a3b8;

      .dash {
        display: inline-block;
        width: 14px;
        border-top: 2px dashed #cbd5e1;
      }
    }
  }
`;

/*
 * The cumulative sales-volume line is drawn with the SVG pathLength trick:
 * normalizing to 1 lets stroke-dashoffset animate the draw without
 * measuring the real path length.
 */
const DrawnPath = styled('path')<{ active: boolean; reduced: boolean }>`
  stroke-dasharray: 1;
  stroke-dashoffset: ${(p) => (p.active || p.reduced ? 0 : 1)};
  transition: stroke-dashoffset 1.6s cubic-bezier(0.22, 1, 0.36, 1) 0.2s;
`;

const FadeShape = styled('polygon')<{ active: boolean; reduced: boolean }>`
  opacity: ${(p) => (p.active || p.reduced ? 1 : 0)};
  transition: opacity 0.9s ease 1s;
`;

const GaugeArc = styled('path')<{
  active: boolean;
  reduced: boolean;
  pct: number;
}>`
  stroke-dasharray: 1;
  stroke-dashoffset: ${(p) => (p.active || p.reduced ? 1 - p.pct : 1)};
  transition: stroke-dashoffset 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.3s;
`;

const GaugeCaption = styled('div')`
  text-align: center;
  font-size: 9.5px;
  color: #94a3b8;
  margin-top: 2px;
`;

const chartYs = [52, 50, 48, 45, 42, 38, 33, 29, 24, 19, 14, 10];
const chartPoints = chartYs
  .map((y, i) => `${(i * 100) / (chartYs.length - 1)},${y}`)
  .join(' ');

export const CampaignManagementMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.3 });

  return (
    <ProductMockupFrame ref={ref}>
      <AppMockupChrome product="Amplify" surface="Campaigns" />
      <ProductMockupBody>
        <Toolbar>
          <CampaignIdentity>
            <div className="name">Fujifilm Promo Q4</div>
            <div className="merchant">
              <MerchantDot bg="#0e1c2b">A</MerchantDot>
              Adorama
              <TypeBadge>Flat spend + CPA</TypeBadge>
            </div>
          </CampaignIdentity>
          <ToolbarRight>
            <StatusLabel>Running</StatusLabel>
            <ToolbarButton>
              Oct 1 – Dec 31, 2026 <ChevronDown size={11} />
            </ToolbarButton>
          </ToolbarRight>
        </Toolbar>

        <CardsRow>
          <MetricCard>
            <div className="label">Clicks</div>
            <div className="value">
              <AnimatedNumber value={142} start={inView} />
            </div>
            <div className="caption">8.3% CTR</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Pageviews</div>
            <div className="value">
              <AnimatedNumber value={1700} start={inView} />
            </div>
            <div className="caption">15 pages live</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Earnings</div>
            <div className="value">
              $
              <AnimatedNumber
                value={1546.1}
                start={inView}
                format={(n) =>
                  n.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })
                }
              />
            </div>
            <div className="caption">50 orders</div>
          </MetricCard>
          <MetricCard>
            <div className="label">
              Sales volume <Target size={10} color={COLORS.PRIMARY} />
            </div>
            <div className="value">
              $
              <AnimatedNumber
                value={30921.9}
                start={inView}
                format={(n) =>
                  n.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })
                }
              />
            </div>
            <div className="caption">of $33,149.17 goal</div>
          </MetricCard>
        </CardsRow>

        <ChartRow>
          <ChartCard>
            <div className="heading">
              Sales volume · cumulative
              <span className="legend">
                <span className="dash" /> Goal
              </span>
            </div>
            <svg
              viewBox="0 0 100 60"
              preserveAspectRatio="none"
              style={{ width: '100%', height: 96, display: 'block' }}
              aria-hidden
            >
              <line
                x1="0"
                y1="7"
                x2="100"
                y2="7"
                stroke="#cbd5e1"
                strokeWidth="0.8"
                strokeDasharray="3 2"
              />
              <FadeShape
                active={inView}
                reduced={reduced}
                points={`0,60 ${chartPoints} 100,60`}
                fill="rgba(46, 92, 255, 0.08)"
              />
              <DrawnPath
                active={inView}
                reduced={reduced}
                d={`M ${chartPoints.split(' ').join(' L ')}`}
                pathLength={1}
                fill="none"
                stroke={COLORS.PRIMARY}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ChartCard>
          <ChartCard>
            <div className="heading">Goal progress</div>
            <svg
              viewBox="0 0 100 56"
              style={{ width: '100%', height: 84, display: 'block' }}
              aria-hidden
            >
              <path
                d="M 12 50 A 38 38 0 0 1 88 50"
                fill="none"
                stroke="#eef1f4"
                strokeWidth="9"
                strokeLinecap="round"
              />
              <GaugeArc
                active={inView}
                reduced={reduced}
                pct={0.933}
                d="M 12 50 A 38 38 0 0 1 88 50"
                pathLength={1}
                fill="none"
                stroke={COLORS.PRIMARY}
                strokeWidth="9"
                strokeLinecap="round"
              />
              <text
                x="50"
                y="44"
                textAnchor="middle"
                fontSize="15"
                fontWeight="700"
                fill="#0e1c2b"
              >
                93.3%
              </text>
              <text
                x="50"
                y="54"
                textAnchor="middle"
                fontSize="6.5"
                fill="#94a3b8"
              >
                of GMV goal
              </text>
            </svg>
            <GaugeCaption>On pace to deliver by Dec 31</GaugeCaption>
          </ChartCard>
        </ChartRow>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
